import { LoadingButton } from '@mui/lab'
import { Alert, Button, Grid, TextField } from '@mui/material'
import React from 'react'

import { TestEvaluationResultsGoldenSignalIntegration } from 'components/services/create/TestEvaluationResultsGoldenSignalIntegration'
import useAuth from 'hooks/use-auth'
import theme from 'theme'
import { EndpointResponse } from 'types/api/end-point'
import { EvaluationResults } from 'types/api/services/evaluation'
import {
  GoldenSignalEvaluationConfiguration,
  TeamExternalIntegration
} from 'types/api/services/golden-signal'
import {
  testTeamExternalIntegrationEndpointConfiguration,
  testStatusCheckEvaluation
} from 'utils/team-external-integrations/team-external-integrations-util'

enum TEST_STATUS_CHECK_ACTIONS {
  TEST_STATUS_CHECK_LOADING = 'testStatusCheckLoading',
  TEST_STATUS_CHECK_RESET = 'testStatusCheckReset',
  TEST_STATUS_CHECK_ERROR = 'testStatusCheckError',
  TEST_STATUS_EVALUATION_ERROR = 'testStatusEvaluationError',
  TEST_STATUS_EVALUATION_SUCCESS = 'testStatusEvaluationSuccess',
  TEST_STATUS_CHECK_CANCELING = 'testStatusCheckCanceling'
}

type TestStatusCheckState = {
  isLoading: boolean
  isCanceling: boolean
  alertMessage: string
  authResponseBody: string
  evaluationResults: EvaluationResults
  endpointResponse: EndpointResponse
}

type TestStatusCheckAction =
  | {
      type: TEST_STATUS_CHECK_ACTIONS.TEST_STATUS_CHECK_LOADING
      isLoading: boolean
    }
  | {
      type: TEST_STATUS_CHECK_ACTIONS.TEST_STATUS_CHECK_CANCELING
      isCanceling: boolean
    }
  | {
      type: TEST_STATUS_CHECK_ACTIONS.TEST_STATUS_CHECK_ERROR
      authResponseBody: string
    }
  | {
      type: TEST_STATUS_CHECK_ACTIONS.TEST_STATUS_EVALUATION_ERROR
    }
  | {
      type: TEST_STATUS_CHECK_ACTIONS.TEST_STATUS_EVALUATION_SUCCESS
      evaluationResults: EvaluationResults
      endpointResponse: EndpointResponse
    }
  | {
      type: TEST_STATUS_CHECK_ACTIONS.TEST_STATUS_CHECK_RESET
    }

const testStatusCheckReducer = (
  state: TestStatusCheckState,
  action: TestStatusCheckAction
): TestStatusCheckState => {
  switch (action.type) {
    case TEST_STATUS_CHECK_ACTIONS.TEST_STATUS_CHECK_LOADING:
      return {
        ...state,
        isLoading: action.isLoading
      }
    case TEST_STATUS_CHECK_ACTIONS.TEST_STATUS_CHECK_CANCELING:
      return {
        ...state,
        alertMessage: 'Canceled',
        isCanceling: action.isCanceling,
        authResponseBody: null,
        evaluationResults: null
      }
    case TEST_STATUS_CHECK_ACTIONS.TEST_STATUS_CHECK_ERROR:
      return {
        ...state,
        isLoading: false,
        alertMessage: 'Unable to Authenticate',
        authResponseBody: action.authResponseBody,
        endpointResponse: null,
        evaluationResults: null
      }
    case TEST_STATUS_CHECK_ACTIONS.TEST_STATUS_EVALUATION_ERROR:
      return {
        ...state,
        isLoading: false,
        isCanceling: false,
        authResponseBody: null,
        endpointResponse: null
      }
    case TEST_STATUS_CHECK_ACTIONS.TEST_STATUS_EVALUATION_SUCCESS:
      return {
        ...state,
        isLoading: false,
        isCanceling: false,
        alertMessage: null,
        authResponseBody: null,
        evaluationResults: action.evaluationResults,
        endpointResponse: action.endpointResponse
      }
    case TEST_STATUS_CHECK_ACTIONS.TEST_STATUS_CHECK_RESET:
      return {
        isLoading: false,
        isCanceling: false,
        alertMessage: null,
        authResponseBody: null,
        evaluationResults: null,
        endpointResponse: null
      }
    default:
      return state
  }
}

const TestStatusCheck: React.FC<{
  disabled: boolean
  integration: TeamExternalIntegration
  evaluationConfiguration: GoldenSignalEvaluationConfiguration
  onSuccess: (endpointResponse: EndpointResponse) => void
}> = ({ disabled, integration, evaluationConfiguration, onSuccess }) => {
  const { currentTeamId } = useAuth()

  const initialState: TestStatusCheckState = {
    isLoading: false,
    isCanceling: false,
    alertMessage: null,
    authResponseBody: null,
    evaluationResults: null,
    endpointResponse: null
  }

  const [testStatusCheckState, dispatch] = React.useReducer(
    testStatusCheckReducer,
    initialState
  )

  React.useEffect(
    () => dispatch({ type: TEST_STATUS_CHECK_ACTIONS.TEST_STATUS_CHECK_RESET }),
    [integration?.type]
  )

  const abortControllerRef = React.useRef<AbortController>()

  return (
    <Grid container data-test-status-check direction="column" rowSpacing={2}>
      <Grid item>
        <LoadingButton
          data-testid="test-status-check"
          disabled={disabled}
          loading={testStatusCheckState.isLoading}
          size="small"
          variant="contained"
          onClick={async () => {
            dispatch({
              type: TEST_STATUS_CHECK_ACTIONS.TEST_STATUS_CHECK_LOADING,
              isLoading: true
            })
            await testTeamExternalIntegrationEndpointConfiguration(
              integration,
              currentTeamId,
              abortControllerRef,
              (response) => {
                testStatusCheckEvaluation(
                  evaluationConfiguration,
                  response,
                  currentTeamId,
                  integration,
                  (evaluationResults) => {
                    dispatch({
                      type: TEST_STATUS_CHECK_ACTIONS.TEST_STATUS_EVALUATION_SUCCESS,
                      evaluationResults: evaluationResults,
                      endpointResponse: response
                    })
                    onSuccess(response)
                  },
                  () => {
                    dispatch({
                      type: TEST_STATUS_CHECK_ACTIONS.TEST_STATUS_EVALUATION_ERROR
                    })
                  }
                )
              },
              (responseBody) => {
                dispatch({
                  type: TEST_STATUS_CHECK_ACTIONS.TEST_STATUS_CHECK_ERROR,
                  authResponseBody: responseBody
                })
              }
            )
          }}
        >
          Test Golden Signal
        </LoadingButton>
        <Button
          color="secondary"
          data-testid="cancel-test-status-check"
          disabled={!testStatusCheckState.isLoading}
          size="small"
          style={{
            display: !testStatusCheckState.isLoading ? 'none' : 'inline',
            marginLeft: theme.space[2]
          }}
          variant="contained"
          onClick={async () => {
            abortControllerRef.current?.abort()
            dispatch({
              type: TEST_STATUS_CHECK_ACTIONS.TEST_STATUS_CHECK_CANCELING,
              isCanceling: true
            })
          }}
        >
          Cancel Test Golden Signal
        </Button>
      </Grid>
      {!testStatusCheckState.isLoading &&
        testStatusCheckState.authResponseBody && (
          <Grid item>
            <TextField
              multiline
              data-testid="test-status-check-integration-response-body"
              disabled={true}
              fullWidth={true}
              id="outlined-multiline-static"
              label="Response"
              rows={4}
              value={testStatusCheckState.authResponseBody}
            />
          </Grid>
        )}
      {!testStatusCheckState.isLoading && testStatusCheckState.alertMessage && (
        <Grid item>
          <Alert data-testid="test-status-check-alert" severity="error">
            {testStatusCheckState.alertMessage}
          </Alert>
        </Grid>
      )}
      {!testStatusCheckState.isLoading &&
        testStatusCheckState.evaluationResults &&
        testStatusCheckState.endpointResponse && (
          <Grid item>
            <TestEvaluationResultsGoldenSignalIntegration
              endpointResponse={testStatusCheckState.endpointResponse}
              evaluationResults={testStatusCheckState.evaluationResults}
              integration={integration}
            />
          </Grid>
        )}
    </Grid>
  )
}

export default TestStatusCheck
